import { useStarredEntities } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import { SidebarGroupLabel } from './SidebarGroupLabel';
import { SidebarNavItem } from './SidebarNavItem';
import { StarIcon } from './navIcons';

/**
 * The entities a reader has starred, in the sidebar (DX-302).
 *
 * The target picture calls this group "Pinned" and draws each entry with a
 * health dot — green, grey or amber. The dot needs a live health signal per
 * entity. The status service answers that one component at a time (DX-308) —
 * asked for every starred entry on every page, and green only where a probe
 * measured; until that is built, no dot, rather than one next to a service
 * nobody has checked.
 *
 * So this is the same idea running on what the portal already knows. Starring
 * is Backstage's own mechanism, already used by the home page's Starred tab
 * and by the star on an entity's header, and it is stored per reader. A
 * reader who stars a service sees it here; the group disappears when they
 * have starred nothing, because an empty group teaches a reader to ignore
 * that part of the column.
 *
 * The heading says "Starred" rather than "Pinned" for the same reason the
 * entries carry no dot: it names what the portal actually does. Renaming it
 * to "Pinned" while the star is what puts things there would mean two words
 * for one action, which is how a menu stops being trustworthy.
 *
 * MUI v4 only — no MUI v5.
 */

export const SidebarStarred = () => {
  const { starredEntities } = useStarredEntities();

  if (starredEntities.size === 0) {
    return null;
  }

  return (
    <>
      <SidebarGroupLabel>Starred</SidebarGroupLabel>
      {[...starredEntities].map(entityRef => {
        // A starred entity is held as a ref string; the catalog is not read
        // here. The name is what a reader recognises — the same text the star
        // sat next to — and reading it from the ref means the group renders
        // without waiting on a request, which is what keeps the sidebar from
        // shifting under a reader as the page settles.
        const { kind, namespace, name } = parseEntityRef(entityRef);
        return (
          <SidebarNavItem
            key={entityRef}
            icon={StarIcon}
            to={`/catalog/${namespace}/${kind}/${name}`}
            text={name}
          />
        );
      })}
    </>
  );
};
